import { z } from "zod";
import { BILLING_HOLD_KINDS } from "../billing/bill.js";
import { periodSchema } from "../billing/period.js";
import { PAID_PLAN_IDS, planIdSchema } from "../billing/plans.js";
import { SUBSCRIPTION_STATUSES } from "../billing/stripe.js";
import {
  POLICY_OUTCOMES,
  actionIdSchema,
  actionSchema,
  actionTemplateSpecSchema,
  BILLING_STATES,
  apiKeyIdSchema,
  conversationIdSchema,
  emailSchema,
  historyEntrySchema,
  inboxSchema,
  inputValuesSchema,
  memberSchema,
  messageIdSchema,
  messageRowSchema,
  outboundIdSchema,
  routeDecisionSchema,
  runIdSchema,
  runSchema,
  sendingDomainSchema,
  tenantSchema,
  traceStepSchema,
  webhookPayloadSchema,
} from "../schemas/index.js";

/** Shapes the control API returns. The console reads its types from here. */

export const inviteViewSchema = z.strictObject({ id: z.string(), email: emailSchema, invitedBy: emailSchema, createdAt: z.iso.datetime() });
export type InviteView = z.infer<typeof inviteViewSchema>;

export const tenantViewSchema = z.strictObject({
  tenant: tenantSchema,
  inboxes: z.array(inboxSchema),
  members: z.array(memberSchema),
  invites: z.array(inviteViewSchema),
  domains: z.array(sendingDomainSchema),
});
export type TenantView = z.infer<typeof tenantViewSchema>;

export const decisionViewSchema = z.strictObject({
  actionId: actionIdSchema,
  actionName: z.string(),
  suggestedActionId: actionIdSchema.optional(),
  confidence: z.number(),
  inputs: inputValuesSchema,
  reason: z.string(),
  alternatives: routeDecisionSchema.shape.alternatives,
  policyOutcome: z.enum(POLICY_OUTCOMES),
  policyReason: z.string().optional(),
});
export type DecisionView = z.infer<typeof decisionViewSchema>;

export const outboundViewSchema = z.strictObject({
  outboundId: outboundIdSchema,
  kind: z.enum(["reply", "forward"]),
  to: emailSchema,
  subject: z.string(),
  textPreview: z.string(),
  state: z.enum(["sent", "delivered", "bounced", "complained"]),
  at: z.iso.datetime(),
});
export type OutboundView = z.infer<typeof outboundViewSchema>;

export const messageDetailSchema = z.strictObject({
  message: messageRowSchema,
  decision: decisionViewSchema.nullable(),
  runs: z.array(runSchema),
  outbound: z.array(outboundViewSchema),
  trace: z.array(traceStepSchema),
});
export type MessageDetail = z.infer<typeof messageDetailSchema>;

/** The full text of a message. It never changes, so a client reads it once. */
export const messageTextSchema = z.strictObject({ text: z.string() });
export type MessageText = z.infer<typeof messageTextSchema>;

export const messageListSchema = z.strictObject({ messages: z.array(messageRowSchema), nextCursor: messageIdSchema.nullable() });
export type MessageList = z.infer<typeof messageListSchema>;

/** A message that a bulk override did not change, for example one still running. */
export const bulkOverrideResultSchema = z.strictObject({
  results: z.array(
    z.discriminatedUnion("outcome", [
      z.strictObject({ messageId: messageIdSchema, outcome: z.literal("changed") }),
      z.strictObject({ messageId: messageIdSchema, outcome: z.literal("skipped"), reason: z.string() }),
    ]),
  ),
});
export type BulkOverrideResult = z.infer<typeof bulkOverrideResultSchema>;

export const conversationViewSchema = z.strictObject({
  id: conversationIdSchema,
  subject: z.string(),
  senderEmail: emailSchema,
  lastMessageAt: z.iso.datetime(),
  history: z.array(historyEntrySchema),
});

export const approvalViewSchema = z.strictObject({
  runId: runIdSchema,
  messageId: messageIdSchema,
  actionId: actionIdSchema,
  actionName: z.string(),
  from: z.string(),
  subject: z.string(),
  createdAt: z.iso.datetime(),
  inputs: inputValuesSchema,
  /** Inputs the member may edit before approving: the ones the writer wrote. */
  editable: z.array(z.string()),
});
export type ApprovalView = z.infer<typeof approvalViewSchema>;

export const usageRowSchema = z.strictObject({
  day: z.string(),
  messages: z.int(),
  runs: z.int(),
  outbound: z.int(),
  writerCalls: z.int(),
  routerCalls: z.int(),
  runsByAction: z.record(z.string(), z.int()),
});
export type UsageRow = z.infer<typeof usageRowSchema>;

export const apiKeyViewSchema = z.strictObject({ id: apiKeyIdSchema, label: z.string(), createdAt: z.iso.datetime(), lastUsedAt: z.iso.datetime().optional() });
export type ApiKeyView = z.infer<typeof apiKeyViewSchema>;

export const actionTestResultSchema = z.strictObject({
  request: webhookPayloadSchema,
  signature: z.string(),
  response: z.strictObject({ status: z.int().nullable(), body: z.string(), latencyMs: z.int(), outcome: z.string() }),
});
export type ActionTestResult = z.infer<typeof actionTestResultSchema>;

export const actionListSchema = z.strictObject({ actions: z.array(actionSchema) });

export const templateListSchema = z.strictObject({
  templates: z.array(z.strictObject({ key: z.string(), title: z.string(), blurb: z.string(), spec: actionTemplateSpecSchema })),
});
export type TemplateList = z.infer<typeof templateListSchema>;

const meteredCountsSchema = z.strictObject({ messages: z.int(), writerCalls: z.int() });

/** The tenant's plan and its usage in the current period. */
export const billingViewSchema = z.strictObject({
  /** `paymentProblem`: a subscription exists but does not pay, so the tenant is on free limits. */
  state: z.enum(BILLING_STATES),
  plan: planIdSchema,
  /** In `paymentProblem`, the plan that the subscription is for. */
  subscribedPlan: z.enum(PAID_PLAN_IDS).nullable(),
  period: periodSchema,
  periodEndsAt: z.iso.datetime(),
  usage: meteredCountsSchema,
  allowance: meteredCountsSchema,
  /** The plan price plus overage so far, in millionths of a dollar. */
  estimatedBillMicros: z.int(),
  billLimitUsd: z.int().nullable(),
  subscriptionStatus: z.enum(SUBSCRIPTION_STATUSES).nullable(),
  cancelAtPeriodEnd: z.boolean(),
  /** True when the tenant has a Stripe customer, so the billing portal can open. */
  hasCustomer: z.boolean(),
  /** Set when mailhand holds new email for the monthly free limit or the bill limit. */
  hold: z.enum(BILLING_HOLD_KINDS).nullable(),
});
export type BillingView = z.infer<typeof billingViewSchema>;
